import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addFavorite, removeFavorite } from '@libs/api-proxy-client';
import { AccountProduct } from '@libs/common-types';
import { ResponseWithParsedData } from '@libs/core';
import { FavoriteItemsSuccessResponse } from '../../types';
import { FAVORITES_QUERY_KEY } from './queryKey';
type FavoriteResponseType = Error | FavoriteItemsSuccessResponse | ResponseWithParsedData<FavoriteItemsSuccessResponse>;
type MutateProps = {
  sku: string;
  userId: string;
  action: 'add' | 'remove';
};
type MutateContext = {
  prevData: FavoriteItemsSuccessResponse | undefined;
  queryKey: readonly [string, string];
};
type MutateResult = {
  response: FavoriteResponseType;
};
type UseToggleFavoriteProps = {
  onAddActionCallback?: () => void;
  onErrorCallback?: (props: {
    error: Error;
    event: 'add' | 'remove';
  }) => void;
};
const throwIfFavoriteMutationFailed = (response: FavoriteResponseType, action: MutateProps['action']) => {
  if (response instanceof Error) {
    throw response;
  }

  // apiFetch returns failed HTTP calls as ResponseWithParsedData (resolved, not thrown).
  if (response instanceof Response && !response.ok) {
    const {
      parsedResponse
    } = response;
    if (parsedResponse && typeof parsedResponse === 'object' && 'detail' in parsedResponse && typeof (parsedResponse as {
      detail: unknown;
    }).detail === 'string') {
      throw new Error((parsedResponse as {
        detail: string;
      }).detail);
    }
    throw new Error(`Failed to ${action} favorite`);
  }
};
const extractFavoriteResponse = (response: FavoriteResponseType): FavoriteItemsSuccessResponse | null => {
  if (response instanceof Error) {
    return null;
  }
  if (response && typeof response === 'object' && 'parsedResponse' in response) {
    const parsed = (response as {
      parsedResponse: unknown;
    }).parsedResponse;
    if (parsed && typeof parsed === 'object' && 'items' in parsed) {
      return parsed as FavoriteItemsSuccessResponse;
    }
    return null;
  }
  if (response && 'items' in response) {
    return response;
  }
  return null;
};
const extractItemsFromCacheData = (data: unknown): AccountProduct[] => {
  if (!data || typeof data !== 'object') {
    return [];
  }
  // Handle error responses (e.g., 401 unauthorized)
  if ('parsedResponse' in data) {
    const parsed = (data as {
      parsedResponse: unknown;
    }).parsedResponse;
    if (parsed && typeof parsed === 'object' && 'items' in parsed && Array.isArray((parsed as {
      items: unknown;
    }).items)) {
      return (parsed as FavoriteItemsSuccessResponse).items;
    }
    return [];
  }
  // Handle success response with items
  if ('items' in data && Array.isArray((data as {
    items: unknown;
  }).items)) {
    return (data as FavoriteItemsSuccessResponse).items;
  }
  return [];
};
export const useToggleFavorite = t0 => {
  const $ = _c(17);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onAddActionCallback,
    onErrorCallback
  } = t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      add: addFavorite,
      remove: removeFavorite
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const requestFn = t2;
  let t3;
  if ($[3] !== queryClient) {
    t3 = async t4 => {
      const {
        action,
        sku,
        userId
      } = t4;
      const queryKey = [FAVORITES_QUERY_KEY, userId] as const;
      await queryClient.cancelQueries({
        queryKey
      });
      const prevData = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        const items = extractItemsFromCacheData(old);
        if (action === "add") {
          const exists = items.some(item => item.sku === sku);
          if (exists) {
            return old && typeof old === "object" && "items" in old ? old : {
              items
            };
          }
          return {
            items: [...items, {
              sku
            } as AccountProduct]
          };
        } else {
          return {
            items: items.filter(item_0 => item_0.sku !== sku)
          };
        }
      });
      return {
        prevData,
        queryKey
      };
    };
    $[3] = queryClient;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onAddActionCallback) {
    t4 = async t5 => {
      const {
        action: action_0,
        sku: sku_0
      } = t5;
      const response = await requestFn[action_0]({
        sku: sku_0
      });
      throwIfFavoriteMutationFailed(response, action_0);
      if (action_0 === "add" && onAddActionCallback) {
        onAddActionCallback();
      }
      return {
        response
      };
    };
    $[5] = onAddActionCallback;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onErrorCallback || $[8] !== queryClient) {
    t5 = (err, vars, ctx) => {
      if (ctx?.prevData !== undefined) {
        queryClient.setQueryData(ctx.queryKey, ctx.prevData);
      }
      if (onErrorCallback) {
        onErrorCallback({
          error: err,
          event: vars.action
        });
      }
    };
    $[7] = onErrorCallback;
    $[8] = queryClient;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== queryClient) {
    t6 = (data, _err, _vars, context) => {
      if (!data?.response || !context?.queryKey) {
        return;
      }
      const favoriteResponse = extractFavoriteResponse(data.response);
      if (favoriteResponse) {
        queryClient.setQueryData(context.queryKey, favoriteResponse);
      }
    };
    $[10] = queryClient;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = {
      onMutate: t3,
      mutationFn: t4,
      onError: t5,
      onSettled: t6
    };
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return useMutation(t7);
};